import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import {
    Alert,
    Anchor,
    Button,
    Group,
    Loader,
    Modal,
    ScrollArea,
    Skeleton,
    Stack,
    Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconChevronLeft, IconTrash } from '@tabler/icons-react';
import { bulkDeleteCandidates, getCandidate } from '../api/candidates';
import { listCpeMappings } from '../api/cpe-mappings';
import { listVendorAliases } from '../api/vendor-aliases';
import { CandidateInfoCard } from '../components/CandidateInfoCard';
import { CpeMappingsCard } from '../components/CpeMappingsCard';
import { OsintReportCard } from '../components/OsintReportCard';
import { PromoteCandidateCard } from '../components/PromoteCandidateCard';
import { VendorAliasesCard } from '../components/VendorAliasesCard';
import { useIsAdmin } from '../hooks/useIsAdmin';

// The `$id` segment is parsed to a number at the route (see router.tsx), so the
// candidate id arrives here already typed. Aliased to `candidateId` to match the
// fetch + card props.
const route = getRouteApi('/candidates/$id');

/**
 * Candidate detail view. Mirrors VendorDetailView's structure (inline
 * breadcrumb + stacked cards) so reviewers see a familiar layout when they
 * pivot between the two sections. Rendered at `/candidates/$id`; the breadcrumb
 * and a successful promote both navigate back to the `/candidates` list.
 */
export const CandidateDetailView = (): React.JSX.Element => {
    const { id: candidateId } = route.useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: adminStatus } = useIsAdmin();
    const isAdmin = adminStatus?.is_admin ?? false;
    const [deleteOpen, setDeleteOpen] = useState(false);

    const goToCandidateList = useCallback(() => {
        void navigate({ to: '/candidates' });
    }, [navigate]);

    // Soft-delete this non-curated candidate. Curated candidates are rejected by
    // the endpoint; those are removed from the vendor detail view instead.
    const deleteMutation = useMutation({
        mutationFn: () => bulkDeleteCandidates([candidateId]),
        onSuccess: (result) => {
            setDeleteOpen(false);
            // The endpoint returns 200 with per-id buckets; a rejected id (e.g. a
            // curated candidate) lands in `failed`, so guard against a false success.
            if (result.failed.length > 0) {
                notifications.show({
                    color: 'red',
                    title: 'Could not delete candidate',
                    message: result.failed[0]?.error ?? 'The candidate could not be deleted.',
                });
                return;
            }
            void queryClient.invalidateQueries({ queryKey: ['candidates'] });
            notifications.show({
                color: 'green',
                title: 'Candidate deleted',
                message: 'The candidate was removed.',
            });
            void navigate({ to: '/candidates' });
        },
        onError: (err: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not delete candidate',
                message: err instanceof Error ? err.message : 'Unknown error',
            });
        },
    });

    const {
        data: candidate,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['candidate', candidateId],
        queryFn: () => getCandidate(candidateId),
    });

    // CPEs can be assigned BEFORE promotion (VI-556 CPE-at-promote): cpe_mapping
    // keys on the candidate id, so the same card used on the vendor detail works
    // here pre-promote — a curated vendor is then CPE-ready without turning on
    // monitoring (which stays an explicit opt-in on the vendor detail view).
    const { data: cpeMappingsData, isLoading: isCpeLoading } = useQuery({
        queryKey: ['cpe-mappings', candidateId],
        queryFn: () => listCpeMappings({ catalog_product_candidate_id: candidateId }),
    });
    const cpeMappings = cpeMappingsData?.data ?? [];

    // Aliases key on the candidate id too (VI-473), so the same card used on the
    // vendor detail works pre-promote. The JIT upsert records requested-name
    // variants here (e.g. "MS Azure" for a stored "Azure"), so reviewers can see
    // and curate them before promotion.
    const { data: aliasesData, isLoading: isAliasesLoading } = useQuery({
        queryKey: ['vendor-aliases', candidateId],
        queryFn: () => listVendorAliases({ catalog_product_candidate_id: candidateId }),
    });
    const aliases = aliasesData?.data ?? [];

    const candidateLabel = candidate
        ? `${candidate.name ?? 'Unnamed candidate'}${candidate.domain ? ` (${candidate.domain})` : ''}`
        : 'Loading…';

    return (
        <Stack gap="xl" flex={1} mih={0} data-testid="CandidateDetailView">
            <Group gap="xs" align="center">
                <Anchor
                    component={Link}
                    to="/candidates"
                    size="sm"
                    data-testid="DetailBreadcrumbBack"
                >
                    <Group gap={4} align="center">
                        <IconChevronLeft size={14} />
                        Candidate Catalog
                    </Group>
                </Anchor>
                <Text size="sm" c="dimmed">
                    /
                </Text>
                <Text size="sm" fw={500}>
                    {candidateLabel}
                </Text>
                {isAdmin && candidate ? (
                    <Button
                        ml="auto"
                        variant="light"
                        color="red"
                        size="xs"
                        leftSection={<IconTrash size={14} />}
                        onClick={() => {
                            setDeleteOpen(true);
                        }}
                        data-testid="CandidateDetailDeleteButton"
                    >
                        Delete
                    </Button>
                ) : null}
            </Group>

            {error ? (
                <Alert
                    icon={<IconAlertTriangle size={16} />}
                    color="red"
                    title="Failed to load candidate"
                    data-testid="CandidateDetailError"
                >
                    {error instanceof Error ? error.message : 'Unknown error'}
                </Alert>
            ) : isLoading || !candidate ? (
                <Skeleton height={300} radius="md" />
            ) : (
                <ScrollArea flex={1} mih={0}>
                    <Stack gap="xl">
                        <CandidateInfoCard candidate={candidate} />
                        <OsintReportCard catalogProductCandidateId={candidateId} />
                        {!isCpeLoading && (
                            <CpeMappingsCard
                                catalogProductCandidateId={candidateId}
                                mappings={cpeMappings}
                            />
                        )}
                        {isAliasesLoading ? (
                            <Loader />
                        ) : (
                            <VendorAliasesCard
                                catalogProductCandidateId={candidateId}
                                aliases={aliases}
                            />
                        )}
                        <PromoteCandidateCard
                            candidate={candidate}
                            onPromoted={goToCandidateList}
                        />
                    </Stack>
                </ScrollArea>
            )}

            <Modal
                opened={deleteOpen}
                onClose={() => {
                    setDeleteOpen(false);
                }}
                centered
                title="Delete candidate"
                data-testid="CandidateDetailDeleteModal"
            >
                <Stack gap="md">
                    <Text size="sm">
                        Delete <strong>{candidate?.name ?? 'this candidate'}</strong>? This removes
                        the non-curated candidate record and can’t be undone from here.
                    </Text>
                    <Group justify="flex-end" gap="sm">
                        <Button
                            variant="default"
                            onClick={() => {
                                setDeleteOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            leftSection={<IconTrash size={16} />}
                            loading={deleteMutation.isPending}
                            onClick={() => {
                                deleteMutation.mutate();
                            }}
                            data-testid="CandidateDetailDeleteConfirm"
                        >
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};
